import { useId } from "react";

/**
 * AnimatedA — draws the brand pyramid in sequence on mount.
 *
 * Extracted verbatim from MainNavbar.jsx (NAVBAR_REMOVAL_PLAN.md §3.1) so the
 * GS floating sidebar toggle and the DD navbar share ONE implementation; the
 * only change is the local `useId` import. Because consumers render it
 * conditionally (closed state only), the draw-in replays on every close —
 * that replay is part of the sidebar's close animation and must be kept.
 *
 * Outer triangle → inner triangle → three horizontal rungs that span
 * the full width of the outer triangle at each y-level.
 *
 * X-coords for the rungs follow the outer triangle's edge equation:
 *   left  edge: x = 3  + (42 − y) × (21/39)
 *   right edge: x = 45 − (42 − y) × (21/39)
 *
 *   y=19  →  x: 14.8–33.2  (rounded to 15–33)
 *   y=25  →  x: 11.5–36.5  (rounded to 11–37)
 *   y=31  →  x:  8.2–39.8  (rounded to  8–40)
 */
function AnimatedA({ size = 24, color = "#1c9dea" }) {
  const uid = useId().replace(/:/g, "");
  const fid = `vng-${uid}`;

  // For polygons/shapes — includes glow filter (works on 2D bounding boxes)
  const s = (dashLen, dur, delay) => ({
    stroke: color,
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    filter: `url(#${fid})`,
    strokeDasharray: dashLen,
    "--d": dashLen,
    animation: `vn-draw-${uid} ${dur}s cubic-bezier(0.16,1,0.3,1) ${delay}s both`,
  });

  // For horizontal lines — NO filter. Percentage-based filter coords collapse
  // to zero on zero-height bounding boxes, making the lines invisible.
  const ls = (dashLen, dur, delay) => ({
    stroke: color,
    fill: "none",
    strokeLinecap: "round",
    strokeDasharray: dashLen,
    "--d": dashLen,
    animation: `vn-draw-${uid} ${dur}s cubic-bezier(0.16,1,0.3,1) ${delay}s both`,
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      <defs>
        <filter id={fid} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <style>{`
          @keyframes vn-draw-${uid} {
            from { stroke-dashoffset: var(--d); opacity: 0; }
            8%   { opacity: 1; }
            to   { stroke-dashoffset: 0; opacity: 1; }
          }
        `}</style>
      </defs>

      {/* ① Outer triangle — 0s */}
      <polygon
        points="24,3 45,42 3,42"
        strokeWidth="2"
        strokeLinejoin="round"
        style={s(130, 0.7, 0)}
      />

      {/* ② Inner triangle (smaller, centered) — +0.4s */}
      <polygon
        points="24,19 31,31 17,31"
        strokeWidth="1.5"
        strokeLinejoin="round"
        style={s(42, 0.5, 0.4)}
      />

      {/* ③④⑤ All start at x=5 (outer triangle far left).
           Top longest → bottom shortest, all left-anchored. */}
      <line
        x1="5"
        y1="19"
        x2="40"
        y2="19"
        strokeWidth="1.2"
        style={ls(35, 0.38, 0.75)}
      />
      <line
        x1="5"
        y1="25"
        x2="31"
        y2="25"
        strokeWidth="1.2"
        style={ls(26, 0.3, 0.75)}
      />
      <line
        x1="5"
        y1="31"
        x2="22"
        y2="31"
        strokeWidth="1.2"
        style={ls(17, 0.22, 0.75)}
      />
    </svg>
  );
}

export default AnimatedA;
